import React from "react";
import { useAppStore } from "../../store/appStore";
import { shallow } from "zustand/shallow";
import {
  startTask,
  getTaskStatus,
  downloadTaskResult,
} from "../../api/TasksApi";
import { useUserState } from "../../context/user";
import { DelDatePicker } from "../common/DelDatePicker";
import {
  ProjectSelectionInput,
  PhaseSelectionInput,
  SearchEmployeeInput,
  DelStatusSelectionInput,
  OrgSelectionInput,
} from "../common/FilterMenu";
import styles from "./ReportingPage.module.css";

export const ReportingPage = () => {
  const { user } = useUserState().currentUser;

  // Zustand state
  const {
    activeTab,
    selectedReportType,
    filterSectionVisible,
    filterSectionAnimating,
    filters,
    filterPhaseList,
    currentTaskId,
    taskStatus,
  } = useAppStore((s) => s.reportingPage, shallow);

  // Zustand actions
  const {
    setActiveTab,
    setSelectedReportType,
    updateFilter,
    clearFilters,
    setFilterPhaseList,
    setCurrentTaskId,
    setTaskStatus,
    setTaskError,
    clearTask,
  } = useAppStore((s) => s.reportingPageActions);

  // Global data
  const allProjectInfo = useAppStore((s) => s.projects.list, shallow);
  const allInactiveProjects = useAppStore(
    (s) => s.projects.inactiveList,
    shallow
  );
  const allEmployees = useAppStore((s) => s.employees.list, shallow);
  const allTerminatedEmployees = useAppStore(
    (s) => s.employees.terminatedList,
    shallow
  );
  const allSearchEmployees = [...allEmployees, ...allTerminatedEmployees];
  // Properly generate unique non-empty org list as [{ value, label }]
  const uniqueOrgs = Array.from(
    new Set(
      allSearchEmployees
        .map((employee) => employee.org)
        .filter((org) => !!org && org.trim() !== "")
    )
  );
  const listOfOrgs = uniqueOrgs.map((org) => ({
    value: org,
    label: org,
  }));
  const showLoading = useAppStore((s) => s.loadingActions.showLoading);
  const hideLoading = useAppStore((s) => s.loadingActions.hideLoading);
  const showNotification = useAppStore(
    (s) => s.notificationActions.showNotification
  );

  // Check if user can see Safety Reports tab
  const canSeeSafetyReports = user?.admin === 1 || user?.safety === 1;

  // Handle report type selection
  const handleReportTypeChange = (e) => {
    const reportType = e.target.value || null;
    setSelectedReportType(reportType);
  };

  // Filter change handlers
  const handleFilterChange = (field, value) => {
    updateFilter({ [field]: value });
  };

  const handleDatePickerDateChange = (field, date) => {
    if (!date) {
      handleFilterChange(field, "");
      return;
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const formattedDate = `${year}-${month}-${day}`;
    handleFilterChange(field, formattedDate);
  };

  const handleSetSelectedProject = (project) => {
    if (!project) {
      handleFilterChange("projectNumber", "");
      handleFilterChange("projectLabel", "");
      setFilterPhaseList([]);
      return;
    }
    setFilterPhaseList(project.phase_list);
    handleFilterChange("projectNumber", project.value);
    handleFilterChange("projectLabel", project.label);
    handleFilterChange("phase", "");
    handleFilterChange("phaseLabel", "");
  };

  const handleSetSelectedPhase = (phase) => {
    if (!phase) {
      handleFilterChange("phase", "");
      handleFilterChange("phaseLabel", "");
      return;
    }
    handleFilterChange("phase", phase.phase_id);
    handleFilterChange("phaseLabel", phase.label);
  };

  const handleSetSelectedProjectManager = (projectManager) => {
    if (!projectManager) {
      handleFilterChange("projectManager", "");
      handleFilterChange("projectManagerName", "");
      return;
    }
    handleFilterChange("projectManager", projectManager.value);
    handleFilterChange("projectManagerName", projectManager.label);
  };

  const handleSetSelectedInvoiceManager = (invoiceManager) => {
    if (!invoiceManager) {
      handleFilterChange("invoiceManager", "");
      handleFilterChange("invoiceManagerName", "");
      return;
    }
    handleFilterChange("invoiceManager", invoiceManager.value);
    handleFilterChange("invoiceManagerName", invoiceManager.label);
  };

  const handleSetSelectedCreatedBy = (employee) => {
    if (!employee) {
      handleFilterChange("createdBy", "");
      handleFilterChange("createdByName", "");
      return;
    }
    handleFilterChange("createdBy", employee.value);
    handleFilterChange("createdByName", employee.label);
  };

  const handleSetSelectedApprovedBy = (employee) => {
    if (!employee) {
      handleFilterChange("approvedBy", "");
      handleFilterChange("approvedByName", "");
      return;
    }
    handleFilterChange("approvedBy", employee.value);
    handleFilterChange("approvedByName", employee.label);
  };

  const handleSetSelectedDelStatus = (status) => {
    if (!status) {
      handleFilterChange("delStatus", "");
      handleFilterChange("delStatusLabel", "");
      return;
    }
    handleFilterChange("delStatus", status.value);
    handleFilterChange("delStatusLabel", status.label);
  };

  const handleSetSelectedOrg = (org) => {
    if (!org) {
      handleFilterChange("org", "");
      handleFilterChange("orgLabel", "");
      return;
    }
    handleFilterChange("org", org.value);
    handleFilterChange("orgLabel", org.label);
  };

  // Build filter params for backend
  const buildFilterParams = () => {
    const backendFilters = {};

    // Map DEL status
    if (filters.delStatus === "0") {
      // Draft
      backendFilters.submit = 0;
      backendFilters.approved = 0;
    } else if (filters.delStatus === "1") {
      // Submitted
      backendFilters.submit = 1;
      backendFilters.approved = 0;
    } else if (filters.delStatus === "2") {
      // Approved
      backendFilters.submit = 1;
      backendFilters.approved = 1;
    }

    // Build nested filters object
    const nestedFilters = {};

    if (filters.createdStartDate)
      nestedFilters.created_date_start = filters.createdStartDate;
    if (filters.createdEndDate)
      nestedFilters.created_date_end = filters.createdEndDate;
    if (filters.delStartDate)
      nestedFilters.del_date_start = filters.delStartDate;
    if (filters.delEndDate) nestedFilters.del_date_end = filters.delEndDate;
    if (filters.submitStartDate)
      nestedFilters.submit_date_start = filters.submitStartDate;
    if (filters.submitEndDate)
      nestedFilters.submit_date_end = filters.submitEndDate;
    if (filters.approvalStartDate)
      nestedFilters.approval_date_start = filters.approvalStartDate;
    if (filters.approvalEndDate)
      nestedFilters.approval_date_end = filters.approvalEndDate;
    if (filters.projectNumber)
      nestedFilters.project_number = filters.projectNumber;
    if (filters.phase) nestedFilters.phase = filters.phase;
    if (filters.projectManager)
      nestedFilters.project_manager = filters.projectManager;
    if (filters.invoiceManager)
      nestedFilters.invoice_manager = filters.invoiceManager;
    if (filters.createdBy) nestedFilters.created_by = filters.createdBy;
    if (filters.approvedBy) nestedFilters.approved_by = filters.approvedBy;

    return {
      user_id: null,
      ...backendFilters,
      filters: nestedFilters,
      global_search: true,
      sort_by: "del_date",
      sort_dir: "asc",
    };
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (!selectedReportType) return;

    showLoading("Generating report...");

    try {
      const filterParams = buildFilterParams();

      // Determine task name
      let taskName = "";
      let params = { filter_params: filterParams };

      if (selectedReportType === "unit_cost") {
        taskName = "del_reports.unit_cost_upload_excel";
        params.split_month = filters.splitMonth;
        params.include_overhead_projects = filters.includeOverheadProjects;
      } else if (selectedReportType === "timesheet") {
        taskName = "del_reports.employee_timesheet_excel";
        params.org_name = filters.org;
      } else if (selectedReportType === "creation_report") {
        taskName = "del_reports.del_creation_report_excel";
      }

      // Start the task
      const response = await startTask(taskName, params);
      const taskId = response.data.task_id;

      setCurrentTaskId(taskId);
      setTaskStatus("pending");

      // Poll for completion
      pollTaskStatus(taskId);
    } catch (error) {
      console.error("Error starting report task:", error);
      showNotification({
        message: "Failed to start report generation",
        type: "error",
      });
      hideLoading();
    }
  };

  // Poll task status
  const pollTaskStatus = async (taskId) => {
    const maxAttempts = 120; // 4 minutes max (2 second intervals)
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await getTaskStatus(taskId);
        const { state, ready, ok, filename, s3_download_url, error: taskError } = response.data;

        setTaskStatus(state.toLowerCase());

        if (ready && ok) {
          // Task completed successfully
          hideLoading();

          // Task finished but produced no file (e.g. no rows matched the filters)
          if (taskError) {
            showNotification({
              message: taskError,
              type: "error",
            });
            clearTask();
            return;
          }

          // Download the result
          try {
            const downloadResponse = await downloadTaskResult(taskId, {
              s3DownloadUrl: s3_download_url,
              filename,
            });

            if (downloadResponse) {
              // Redis path: create blob and trigger download
              const blob = new Blob([downloadResponse.data]);
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = filename || "report.xlsx";
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }

            showNotification({
              message: "Report downloaded successfully!",
              type: "success",
            });
            clearTask();
          } catch (downloadError) {
            console.error("Download error:", downloadError);
            showNotification({
              message: "Failed to download report",
              type: "error",
            });
            clearTask();
          }
        } else if (state === "FAILURE" || state === "REVOKED") {
          // Task failed
          hideLoading();
          setTaskError(taskError || "Task failed");
          showNotification({
            message: taskError || "Report generation failed",
            type: "error",
          });
          clearTask();
        } else if (attempts < maxAttempts) {
          // Still processing, poll again
          attempts++;
          setTimeout(poll, 2000);
        } else {
          // Timeout
          hideLoading();
          showNotification({
            message: "Report generation timed out",
            type: "error",
          });
          clearTask();
        }
      } catch (error) {
        console.error("Error polling task status:", error);
        hideLoading();
        showNotification({
          message: "Error checking report status",
          type: "error",
        });
        clearTask();
      }
    };

    poll();
  };

  // Clear all filters
  const handleClearFilters = () => {
    clearFilters();
  };

  return (
    <div className={styles.reportingPageContainer}>
      {/* Tab Navigation */}
      <div className={styles.tabNavigation}>
        <button
          className={`${styles.tabButton} ${
            activeTab === "del_reports" ? styles.active : ""
          }`}
          onClick={() => setActiveTab("del_reports")}
        >
          DEL Reports
        </button>
        {canSeeSafetyReports && (
          <button
            className={`${styles.tabButton} ${
              activeTab === "safety_reports" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("safety_reports")}
          >
            Safety Reports
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className={styles.tabContent}>
        {activeTab === "del_reports" && (
          <>
            {/* Report Type Selection */}
            <div className={styles.reportTypeSection}>
              <label className={styles.reportTypeLabel}>
                Select Report Type:
              </label>
              <select
                className={styles.reportTypeDropdown}
                value={selectedReportType || ""}
                onChange={handleReportTypeChange}
              >
                <option value="">-- Choose a Report --</option>
                <option value="unit_cost">Unit Cost Upload Excel</option>
                <option value="timesheet">Employee Timesheet Excel</option>
                <option value="creation_report">DEL Creation Report</option>
              </select>
            </div>

            {!filterSectionVisible && null}

            {/* Filter Section - COMET IMPACT */}
            {filterSectionVisible && (
              <div
                className={`${styles.filterSection} ${
                  filterSectionAnimating ? styles.entering : ""
                }`}
              >
                <h3 className={styles.filterSectionTitle}>Report Filters</h3>

                <div className={styles.filterGrid}>
                  {/* Column 1: Date Filters */}
                  <div className={styles.filterColumn}>
                    <div className={styles.filterColumnTitle}>Date Filters</div>

                    <div className={styles.filterRow}>
                      <label>Created Date:</label>
                      <div className={styles.datePickerGroup}>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>Start:</span>
                          <DelDatePicker
                            className={styles.datePicker}
                            selected={filters.createdStartDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange(
                                "createdStartDate",
                                date
                              )
                            }
                            selectsStart
                            startDate={filters.createdStartDate || ""}
                            endDate={filters.createdEndDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            maxDate={filters.createdEndDate || ""}
                          />
                        </div>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>End:</span>
                          <DelDatePicker
                            selected={filters.createdEndDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange("createdEndDate", date)
                            }
                            selectsEnd
                            startDate={filters.createdStartDate || ""}
                            endDate={filters.createdEndDate || ""}
                            minDate={filters.createdStartDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                          />
                        </div>
                      </div>
                    </div>

                    <div className={styles.filterRow}>
                      <label>DEL Date:</label>
                      <div className={styles.datePickerGroup}>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>Start:</span>
                          <DelDatePicker
                            selected={filters.delStartDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange("delStartDate", date)
                            }
                            selectsStart
                            startDate={filters.delStartDate || ""}
                            endDate={filters.delEndDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                            maxDate={filters.delEndDate || ""}
                          />
                        </div>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>End:</span>
                          <DelDatePicker
                            selected={filters.delEndDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange("delEndDate", date)
                            }
                            selectsEnd
                            startDate={filters.delStartDate || ""}
                            endDate={filters.delEndDate || ""}
                            minDate={filters.delStartDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                          />
                        </div>
                      </div>
                    </div>

                    <div className={styles.filterRow}>
                      <label>Submit Date:</label>
                      <div className={styles.datePickerGroup}>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>Start:</span>
                          <DelDatePicker
                            selected={filters.submitStartDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange(
                                "submitStartDate",
                                date
                              )
                            }
                            selectsStart
                            startDate={filters.submitStartDate || ""}
                            endDate={filters.submitEndDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                            maxDate={filters.submitEndDate || ""}
                          />
                        </div>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>End:</span>
                          <DelDatePicker
                            selected={filters.submitEndDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange("submitEndDate", date)
                            }
                            selectsEnd
                            startDate={filters.submitStartDate || ""}
                            endDate={filters.submitEndDate || ""}
                            minDate={filters.submitStartDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                          />
                        </div>
                      </div>
                    </div>

                    <div className={styles.filterRow}>
                      <label>Approval Date:</label>
                      <div className={styles.datePickerGroup}>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>Start:</span>
                          <DelDatePicker
                            selected={filters.approvalStartDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange(
                                "approvalStartDate",
                                date
                              )
                            }
                            selectsStart
                            startDate={filters.approvalStartDate || ""}
                            endDate={filters.approvalEndDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                            maxDate={filters.approvalEndDate || ""}
                          />
                        </div>
                        <div className={styles.datePickerRow}>
                          <span className={styles.dateLabel}>End:</span>
                          <DelDatePicker
                            selected={filters.approvalEndDate || ""}
                            onChange={(date) =>
                              handleDatePickerDateChange(
                                "approvalEndDate",
                                date
                              )
                            }
                            selectsEnd
                            startDate={filters.approvalStartDate || ""}
                            endDate={filters.approvalEndDate || ""}
                            minDate={filters.approvalStartDate || ""}
                            dateFormat="MM/dd/yyyy"
                            placeholderText="mm/dd/yyyy"
                            className={styles.datePicker}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Column 2: DEL Status & Project Info */}
                  <div className={styles.filterColumn}>
                    <div className={styles.filterColumnTitle}>
                      DEL Status & Project
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="delStatus">DEL Status:</label>
                      <DelStatusSelectionInput
                        selectedDelStatusValue={filters.delStatus}
                        selectedDelStatusLabel={filters.delStatusLabel}
                        setSelectedDelStatusFunction={
                          handleSetSelectedDelStatus
                        }
                        options={[
                          { value: "", label: "All" },
                          { value: "0", label: "Draft" },
                          { value: "1", label: "Submitted" },
                          { value: "2", label: "Approved" },
                        ]}
                      />
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="projectNumber">Project:</label>
                      <ProjectSelectionInput
                        allProjectInfo={allProjectInfo}
                        allInactiveProjects={allInactiveProjects}
                        selectedProjectValue={filters?.projectNumber}
                        selectedProjectLabel={filters?.projectLabel}
                        selectedProjectPhaseList={filters?.projectPhaseList}
                        setSelectedProjectFunction={handleSetSelectedProject}
                      />
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="phase">Phase:</label>
                      <PhaseSelectionInput
                        projectPhases={filterPhaseList}
                        selectedPhaseValue={filters?.phase}
                        selectedPhaseLabel={filters?.phaseLabel}
                        setSelectedPhaseFunction={handleSetSelectedPhase}
                      />
                    </div>

                    {/* Unit Cost only options */}
                    {selectedReportType === "unit_cost" && (
                      <>
                        <div className={styles.checkboxRow}>
                          <input
                            type="checkbox"
                            id="splitMonth"
                            checked={filters?.splitMonth}
                            onChange={(e) =>
                              handleFilterChange("splitMonth", e.target.checked)
                            }
                          />
                          <label htmlFor="splitMonth">
                            Split by Month/Year
                          </label>
                        </div>
                        <div className={styles.checkboxRow}>
                          <input
                            type="checkbox"
                            id="includeOverheadProjects"
                            checked={filters?.includeOverheadProjects}
                            onChange={(e) =>
                              handleFilterChange(
                                "includeOverheadProjects",
                                e.target.checked
                              )
                            }
                          />
                          <label htmlFor="includeOverheadProjects">
                            Include Overhead Projects
                          </label>
                        </div>
                      </>
                    )}
                    {/* Org Selection - Only for timesheet report */}
                    {selectedReportType === "timesheet" && (
                      <div className={styles.filterRow}>
                        <label htmlFor="org">Org:</label>
                        <OrgSelectionInput
                          listOfOrgs={listOfOrgs}
                          selectedOrgValue={filters?.org}
                          selectedOrgLabel={filters?.orgLabel}
                          setSelectedOrgFunction={handleSetSelectedOrg}
                        />
                      </div>
                    )}
                  </div>

                  {/* Column 3: Employee Filters */}
                  <div className={styles.filterColumn}>
                    <div className={styles.filterColumnTitle}>
                      Employee Filters
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="projectManager">Project Manager:</label>
                      <SearchEmployeeInput
                        allEmployees={allSearchEmployees}
                        selectedEmployeeValue={filters?.projectManager}
                        selectedEmployeeLabel={filters?.projectManagerName}
                        setSelectedEmployeeFunction={
                          handleSetSelectedProjectManager
                        }
                      />
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="invoiceManager">Invoice Manager:</label>
                      <SearchEmployeeInput
                        allEmployees={allSearchEmployees}
                        selectedEmployeeValue={filters?.invoiceManager}
                        selectedEmployeeLabel={filters?.invoiceManagerName}
                        setSelectedEmployeeFunction={
                          handleSetSelectedInvoiceManager
                        }
                      />
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="createdBy">Created By:</label>
                      <SearchEmployeeInput
                        allEmployees={allSearchEmployees}
                        selectedEmployeeValue={filters?.createdBy}
                        selectedEmployeeLabel={filters?.createdByName}
                        setSelectedEmployeeFunction={handleSetSelectedCreatedBy}
                      />
                    </div>

                    <div className={styles.filterRow}>
                      <label htmlFor="approvedBy">Approved By:</label>
                      <SearchEmployeeInput
                        allEmployees={allSearchEmployees}
                        selectedEmployeeValue={filters?.approvedBy}
                        selectedEmployeeLabel={filters?.approvedByName}
                        setSelectedEmployeeFunction={
                          handleSetSelectedApprovedBy
                        }
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.filterActions}>
                      <button
                        className={styles.clearButton}
                        onClick={handleClearFilters}
                      >
                        Clear Filters
                      </button>
                      <button
                        className={styles.generateButton}
                        onClick={handleGenerateReport}
                        disabled={!selectedReportType || currentTaskId !== null}
                      >
                        {currentTaskId ? "Generating..." : "Generate Report"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === "safety_reports" && (
          <div>Safety Reports Coming Soon</div>
        )}
      </div>
    </div>
  );
};
